import React, { useEffect, useState } from 'react';
import { AdminUserRow, AdminUserSummary, getAdminUserSummary, UserActivity } from '../../../api/services/adminUsersService';
import AppJourney from './AppJourney';
import { ActionRow, Avatar as DgAvatar, Band, CloseButton, ICONS, LineIcon, SectionLabel, StatusPill } from '../../../components/ui/dg';

import { PLATFORM_LABEL } from '../../../api/services/activityService';

const photoUrl = (url?: string | null) => (!url ? null : /^https?:\/\//.test(url) ? url : `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`);

/**
 * Panneau latéral de la section Utilisateurs : le compte en un coup d'œil
 * (chiffres, vérification, compte, activité récente). On agit depuis la
 * fiche complète : chaque bouton l'ouvre sur le bon onglet ou l'action dépliée.
 * À côté de la table sur grand écran, en tiroir au-dessus sinon.
 */

export const ROLE: Record<string, { label: string; tone: string }> = {
  user: { label: 'Utilisateur', tone: 'bg-gray-100 text-gray-700' },
  moderator: { label: 'Modérateur', tone: 'bg-sky-50 text-sky-700' },
  admin: { label: 'Admin', tone: 'bg-indigo-50 text-indigo-700' }
};

export const SOURCE: Record<string, string> = { local: 'E-mail', google: 'Google', apple: 'Apple' };

export const VERIFICATION: Record<string, { label: string; tone: string }> = {
  verified: { label: 'Vérifié', tone: 'bg-green-50 text-green-700' },
  pending: { label: 'En attente', tone: 'bg-amber-50 text-amber-800' },
  none: { label: 'Non vérifié', tone: 'bg-gray-100 text-gray-600' }
};

const DOCUMENT: Record<string, string> = {
  identity: 'Pièce d’identité',
  license: 'Permis de conduire',
  address: 'Justificatif de domicile',
  selfie: 'Photo de vérification'
};

const DOC_STATUS: Record<string, { label: string; tone: string }> = {
  approved: { label: 'Validé', tone: 'bg-green-50 text-green-700' },
  pending: { label: 'À vérifier', tone: 'bg-amber-50 text-amber-800' },
  rejected: { label: 'Refusé', tone: 'bg-red-50 text-red-700' }
};

const LANG: Record<string, string> = { fr: 'Français', ar: 'Arabe', en: 'Anglais' };

export const fullName = (u: Pick<AdminUserRow, 'first_name' | 'last_name' | 'email'>) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || 'Compte sans nom';

export const Pill = ({ children, tone = 'bg-gray-100 text-gray-700', dot = false }: { children: React.ReactNode; tone?: string; dot?: boolean }) => (
  <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium ${tone}`}>
    {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
    {children}
  </span>
);

/**
 * Badge « compte vérifié », à côté du nom : sceau festonné, dégradé
 * turquoise → vert, coche blanche. Le dégradé est en coordonnées absolues
 * pour courir d'un bout à l'autre du sceau.
 */
const SEAL_LOBES = Array.from({ length: 8 }, (_, i) => {
  const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
  return { cx: 12 + Math.cos(angle) * 7, cy: 12 + Math.sin(angle) * 7 };
});
let sealIds = 0;

export const VerifiedBadge = ({ size = 16 }: { size?: number }) => {
  const [id] = useState(() => `verified-seal-${(sealIds += 1)}`);
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className="shrink-0" role="img" aria-label="Compte vérifié">
      <title>Compte vérifié</title>
      <defs>
        <linearGradient id={id} x1="4" y1="2" x2="20" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#4FC8EC" />
          <stop offset="1" stopColor="#6EE7A0" />
        </linearGradient>
      </defs>
      <g fill={`url(#${id})`}>
        <circle cx="12" cy="12" r="8" />
        {SEAL_LOBES.map((l, i) => <circle key={i} cx={l.cx} cy={l.cy} r="4.1" />)}
      </g>
      <path d="M8.2 12.3l2.6 2.6 5-5.2" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** « Aujourd’hui, 10:24 », « Hier, 18:02 », sinon « 12 sept. 2026 ». */
export const formatMoment = (value?: string | null, empty = '—') => {
  if (!value) return empty;
  const date = new Date(value);
  if (isNaN(date.getTime())) return empty;
  const now = new Date();
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay(date, now)) return `Aujourd’hui, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `Hier, ${time}`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ago = (value: string) => {
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} j`;
  return `le ${new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

const ACTIVITY: Record<UserActivity['kind'], { text: (a: UserActivity) => string; tone: string; icon: string }> = {
  listing_created: { text: (a) => `a publié une annonce${a.label ? ` : ${a.label}` : ''}`, tone: 'bg-indigo-50 text-indigo-600', icon: 'M4 16l1.5-5A2 2 0 0 1 7.4 9.5h9.2a2 2 0 0 1 1.9 1.5L20 16M4 16h16v3H4zM7 19v1M17 19v1' },
  rental_requested: { text: (a) => `a demandé une location${a.label ? ` : ${a.label}` : ''}`, tone: 'bg-sky-50 text-sky-600', icon: 'M8 3v3M16 3v3M4 8h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z' },
  rental_completed: { text: (a) => `a terminé un trajet${a.label ? ` : ${a.label}` : ''}`, tone: 'bg-green-50 text-green-600', icon: 'M5 12.5l4.5 4.5L19 7.5' },
  review_written: { text: (a) => `a laissé un avis${a.label ? ` (${a.label}/5)` : ''}`, tone: 'bg-amber-50 text-amber-600', icon: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5z' },
  document_sent: { text: (a) => `a envoyé un document${a.label ? ` : ${(DOCUMENT[a.label] || a.label).toLowerCase()}` : ''}`, tone: 'bg-violet-50 text-violet-600', icon: 'M7 3h7l5 5v13H7zM14 3v5h5' },
  registered: { text: () => 'a créé son compte Dingo', tone: 'bg-gray-100 text-gray-600', icon: 'M12 5v14M5 12h14' }
};

const Glyph = ({ d, className = 'h-4 w-4' }: { d: string; className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>
);

/**
 * Section qui se plie et se déplie. L'état choisi est retenu (par section,
 * pour toutes les fiches) : qui replie « Compte » une fois ne le revoit plus
 * ouvert à chaque compte.
 */
const FOLD_KEY = 'dingo.users.panel.folds';
const readFolds = (): Record<string, boolean> => {
  try { return JSON.parse(localStorage.getItem(FOLD_KEY) || '{}') || {}; } catch { return {}; }
};

const Fold = ({ id, title, aside, defaultOpen = false, children }: { id: string; title: string; aside?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode }) => {
  const [open, setOpen] = useState(() => {
    const saved = readFolds()[id];
    return typeof saved === 'boolean' ? saved : defaultOpen;
  });
  const toggle = () => {
    setOpen((value) => {
      try { localStorage.setItem(FOLD_KEY, JSON.stringify({ ...readFolds(), [id]: !value })); } catch { /* sans conséquence */ }
      return !value;
    });
  };
  const bodyId = `fold-${id}`;
  return (
    <section>
      <button type="button" onClick={toggle} aria-expanded={open} aria-controls={bodyId} className="flex w-full items-center gap-2 px-[22px] py-3 text-left hover:bg-[#FBFBFE]">
        <h3 className="flex-1 text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">{title}</h3>
        {aside}
        <Glyph d="M9 6l6 6-6 6" className={`h-4 w-4 shrink-0 text-[#9CA3AF] transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div id={bodyId} className="px-[22px] pb-3.5">{children}</div>}
    </section>
  );
};

type FullTab = 'overview' | 'fleet' | 'trips' | 'reviews' | 'earnings';
type FullAction = 'notify' | 'suspend' | 'delete' | null;

interface Props {
  userId: string;
  token: string;
  canSuspend: boolean;
  onClose: () => void;
  onOpenFull: (user: AdminUserRow, tab?: FullTab, action?: FullAction) => void;
  onSeeTrips: (user: AdminUserRow) => void;
}

const UserSidePanel = ({ userId, token, canSuspend, onClose, onOpenFull, onSeeTrips }: Props) => {
  const [data, setData] = useState<AdminUserSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    getAdminUserSummary(token, userId, controller.signal)
      .then(setData)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message || 'Compte indisponible.'); });
    return () => controller.abort();
  }, [token, userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (contactOpen || moreOpen) { setContactOpen(false); setMoreOpen(false); return; }
      if (!document.querySelector('[role="dialog"]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [contactOpen, moreOpen, onClose]);

  useEffect(() => {
    if (!contactOpen && !moreOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-panel-menu]')) { setContactOpen(false); setMoreOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [contactOpen, moreOpen]);

  const u = data?.user;
  const rating = u && Number(u.total_reviews) > 0 ? Number(u.average_rating).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : null;
  const phoneHref = u?.phone ? `tel:${u.phone.replace(/[^\d+]/g, '')}` : null;


  const documents = data?.documents || [];
  const activity = data?.activity || [];
  const pendingDocs = documents.filter((d) => d.status === 'pending').length;

  const body = (
    <>
      <div className="flex justify-end px-[18px] pt-3.5">
        <CloseButton onClick={onClose} label="Fermer le panneau" />
      </div>
      {!u ? (
        error ? <p className="px-6 py-10 text-center text-[13px] text-[#B91C1C]">{error}</p> : (
          <div className="flex flex-col items-center gap-3 px-6 pb-6"><div className="h-[76px] w-[76px] animate-pulse rounded-full bg-[#EEF0F5]" /><div className="h-5 w-40 animate-pulse rounded bg-[#EEF0F5]" /><div className="h-4 w-28 animate-pulse rounded bg-[#EEF0F5]" /></div>
        )
      ) : (
        <>
          {/* Identité : l'avatar dans son anneau, comme le drawer de l'app */}
          <div className="px-[22px] pb-4 text-center">
            <div className="inline-flex"><DgAvatar name={fullName(u)} photo={photoUrl(u.profile_photo)} size={66} ring /></div>
            <b className="mt-2.5 flex items-center justify-center gap-1.5 text-[20px] font-extrabold text-[#1E293B]">
              <span className="truncate">{fullName(u)}</span>
              {u.verification === 'verified' && <VerifiedBadge size={18} />}
            </b>
            <div className="mt-2 flex flex-wrap justify-center gap-1.5">
              <StatusPill tone={u.role === 'admin' ? 'indigo' : u.role === 'moderator' ? 'indigo' : 'grey'}>{ROLE[u.role]?.label || u.role}</StatusPill>
              {u.is_suspended && <StatusPill tone="red">Bloqué</StatusPill>}
              {u.verification === 'verified' && <StatusPill tone="green">Permis vérifié</StatusPill>}
              {u.verification === 'pending' && <StatusPill tone="amber">Permis à vérifier</StatusPill>}
            </div>
          </div>
          <div className="flex gap-2 px-[22px] pb-[18px]">
            {[
              { value: String(u.trips), label: u.trips > 1 ? 'locations' : 'location', run: () => onSeeTrips(u) },
              { value: String(u.listings), label: u.listings > 1 ? 'annonces' : 'annonce', run: () => onOpenFull(u, 'fleet') },
              { value: rating || '—', label: rating ? `note · ${u.total_reviews} avis` : 'note', run: () => onOpenFull(u, 'reviews') }
            ].map((t) => (
              <button key={t.label} type="button" onClick={t.run} className="flex-1 rounded-[14px] bg-[#F7F7FB] px-2 py-3 text-center hover:bg-[#F1F2F8]">
                <b className="block text-[20px] font-extrabold text-[#1E293B]">{t.value}</b>
                <span className="block truncate text-[11.5px] text-[#6B7280]">{t.label}</span>
              </button>
            ))}
          </div>

          <Band />
          <div className="px-[22px] pb-1 pt-3.5"><SectionLabel>Coordonnées</SectionLabel></div>
          {[
            u.email ? { icon: ICONS.mail, text: u.email, href: `mailto:${u.email}` } : null,
            u.phone ? { icon: ICONS.phone, text: u.phone, href: phoneHref } : null,
            { icon: ICONS.user, text: `${SOURCE[u.auth_provider] || u.auth_provider}${u.lang ? ` · app en ${(LANG[u.lang] || u.lang).toLowerCase()}` : ''}`, href: null }
          ].filter(Boolean).map((c: any, i) => (
            <a key={c.text} href={c.href || undefined} className="mx-[22px] flex items-center gap-3 py-2.5 hover:opacity-80" style={{ borderTop: i ? '1px solid #EEF0F4' : 'none' }}>
              <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#F1F2F8]"><LineIcon d={c.icon} size={14} color="#475569" /></span>
              <span className="truncate text-[13.5px] text-[#1E293B]">{c.text}</span>
            </a>
          ))}

          <Band className="mt-2" />
          <div className="px-[22px] pb-1 pt-3.5"><SectionLabel>Actions</SectionLabel></div>
          <ActionRow first icon="message" title="Envoyer un message" subtitle="Une notification dans l’app" onClick={() => onOpenFull(u, 'overview', 'notify')} />
          <ActionRow icon="journal" tone="grey" title="Voir ses locations" subtitle={`${u.trips} ${u.trips > 1 ? 'trajets' : 'trajet'}`} onClick={() => onSeeTrips(u)} />
          <ActionRow icon="doc" tone="grey" title="Fiche complète" subtitle="Identité, annonces, avis, revenus" onClick={() => onOpenFull(u)} />
          {canSuspend && (
            <ActionRow icon="lock" tone={u.is_suspended ? 'grey' : 'red'}
              title={u.is_suspended ? 'Réactiver le compte' : 'Bloquer le compte'}
              subtitle={u.is_suspended ? 'Il retrouve l’usage normal de l’app' : 'Connecté mais en lecture seule'}
              onClick={() => onOpenFull(u, 'overview', u.is_suspended ? null : 'suspend')} />
          )}

          <Band className="mt-2" />
          <div className="divide-y divide-[#EEF0F4]">
            <Fold
              id="verification"
              title="Vérification"
              defaultOpen={u.verification === 'pending'}
              aside={pendingDocs ? <StatusPill tone="amber">{pendingDocs} à vérifier</StatusPill> : null}
            >
              <ul>
                <li className="flex items-center justify-between gap-3 py-2 text-[13px]">
                  <span className="text-[#4B5563]">Adresse e-mail</span>
                  {u.email_verified_at
                    ? <span className="text-[#6B7280]">confirmée {formatMoment(u.email_verified_at).toLowerCase()}</span>
                    : <StatusPill tone="grey">Non confirmée</StatusPill>}
                </li>
                {documents.map((d) => (
                  <li key={d.id_verification} className="flex items-center justify-between gap-3 border-t border-[#EEF0F4] py-2 text-[13px]">
                    <span className="min-w-0">
                      <span className="block truncate text-[#1E293B]">{DOCUMENT[d.type] || d.type}</span>
                      <span className="block text-[11.5px] text-[#9CA3AF]">envoyé {formatMoment(d.created_at).toLowerCase()}</span>
                    </span>
                    <StatusPill tone={d.status === 'approved' ? 'green' : d.status === 'rejected' ? 'red' : 'amber'}>{(DOC_STATUS[d.status] || { label: d.status }).label}</StatusPill>
                  </li>
                ))}
                {documents.length === 0 && <li className="border-t border-[#EEF0F4] py-2 text-[13px] text-[#6B7280]">Aucun document envoyé.</li>}
              </ul>
            </Fold>

            <Fold id="compte" title="Compte">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[13px]">
                <dt className="text-[#6B7280]">Inscription</dt><dd className="text-right text-[#1E293B]">{formatMoment(u.registration_date)}</dd>
                <dt className="text-[#6B7280]">Dernière connexion</dt><dd className="text-right text-[#1E293B]">{formatMoment(u.last_login, 'Jamais')}</dd>
                <dt className="text-[#6B7280]">Téléphone</dt>
                <dd className="text-right text-[#1E293B]">{u.last_platform ? `${PLATFORM_LABEL[u.last_platform] || u.last_platform}${u.last_app_version ? ` · app ${u.last_app_version}` : ''}` : 'Inconnu'}</dd>
                <dt className="text-[#6B7280]">Dernière activité</dt><dd className="text-right text-[#1E293B]">{formatMoment(u.last_seen_at, 'Inconnue')}</dd>
              </dl>
            </Fold>

            <Fold id="parcours" title="Parcours dans l’app">
              <AppJourney token={token} userId={u.id_user} />
            </Fold>

            <Fold id="activite" title="Activité récente" aside={activity[0] ? <span className="text-[12px] text-[#9CA3AF]">{ago(activity[0].at)}</span> : null}>
              {activity.length === 0 ? (
                <p className="text-[13px] text-[#6B7280]">Rien pour l’instant.</p>
              ) : (
                <ol className="space-y-2.5">
                  {activity.map((a, i) => {
                    const meta = ACTIVITY[a.kind];
                    if (!meta) return null;
                    return (
                      <li key={`${a.kind}${a.ref}${i}`} className="flex items-start gap-2.5">
                        <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${meta.tone}`}><Glyph d={meta.icon} className="h-3 w-3" /></span>
                        <span className="min-w-0 text-[13px] leading-snug">
                          <span className="text-[#1E293B]"><span className="font-semibold">{u.first_name || fullName(u)}</span> {meta.text(a)}</span>
                          <span className="block text-[11.5px] text-[#9CA3AF]" title={new Date(a.at).toLocaleString('fr-FR')}>{ago(a.at)}</span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Fold>
          </div>
          <div className="h-3" />
        </>
      )}
    </>
  );

  return (
    <>
      {/* Grand écran : colonne collante à droite de la table */}
      <aside className="hidden h-full overflow-y-auto rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] xl:block" aria-label="Résumé du compte">
        {body}
      </aside>
      {/* Plus petit : tiroir au-dessus de la page */}
      <div className="fixed inset-0 z-40 xl:hidden">
        <button type="button" aria-label="Fermer le panneau" onClick={onClose} className="absolute inset-0 bg-gray-900/30" />
        <aside className="absolute inset-y-0 right-0 w-full max-w-[400px] overflow-y-auto bg-white shadow-2xl" aria-label="Résumé du compte">
          {body}
        </aside>
      </div>
    </>
  );
};

export default UserSidePanel;
